"use client";

import { useState, useTransition } from "react";
import { toast } from "sonner";
import { Lock, Plus, Unlock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { RestorationExamSession } from "@/lib/server-actions/restoration/_shared";
import { createExamSession, toggleExamSessionLock } from "@/lib/server-actions/restoration/examSessions";

interface SessionAdminBarProps {
  currentSession: RestorationExamSession | null;
  onChanged: () => void;
}

/**
 * JCG-1572: 관리자용 세션 관리 바.
 * - 회차 생성 (createExamSession): label + sort_order
 * - 현재 회차 글쓰기 잠금 토글 (toggleExamSessionLock)
 *
 * 관리자 전용. `RestorationPageClient`가 `isAdmin` 체크 후 렌더.
 */
export default function SessionAdminBar({
  currentSession,
  onChanged,
}: SessionAdminBarProps) {
  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState("");
  const [sortOrder, setSortOrder] = useState<string>("0");
  const [isPending, startTransition] = useTransition();

  const handleCreate = () => {
    const trimmed = label.trim();
    if (!trimmed) {
      toast.error("회차 이름을 입력해주세요.");
      return;
    }
    const parsedSort = Number(sortOrder);
    startTransition(async () => {
      const result = await createExamSession(
        trimmed,
        Number.isFinite(parsedSort) ? parsedSort : 0
      );
      if (result.success) {
        toast.success(`회차 "${result.session.label}"가 생성되었습니다.`);
        setLabel("");
        setSortOrder("0");
        setShowCreate(false);
        onChanged();
      } else {
        toast.error(result.error || "회차 생성에 실패했습니다.");
      }
    });
  };

  const handleToggleLock = () => {
    if (!currentSession) return;
    const nextLocked = !currentSession.is_write_locked;
    startTransition(async () => {
      const result = await toggleExamSessionLock(currentSession.id, nextLocked);
      if (result.success) {
        toast.success(
          nextLocked ? "글쓰기가 잠겼습니다." : "글쓰기 잠금이 해제되었습니다."
        );
        onChanged();
      } else {
        toast.error(result.error || "잠금 상태 변경에 실패했습니다.");
      }
    });
  };

  return (
    <div className="rounded-md border border-amber-200 dark:border-amber-900/60 bg-amber-50/50 dark:bg-amber-950/20 p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wider">
          관리자 세션 관리
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          {currentSession && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleToggleLock}
              disabled={isPending}
            >
              {currentSession.is_write_locked ? (
                <>
                  <Unlock className="w-3 h-3 mr-1" />
                  잠금 해제
                </>
              ) : (
                <>
                  <Lock className="w-3 h-3 mr-1" />
                  글쓰기 잠금
                </>
              )}
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant={showCreate ? "outline" : "default"}
            onClick={() => setShowCreate((v) => !v)}
            disabled={isPending}
          >
            {showCreate ? (
              <>
                <X className="w-3 h-3 mr-1" />
                닫기
              </>
            ) : (
              <>
                <Plus className="w-3 h-3 mr-1" />
                회차 추가
              </>
            )}
          </Button>
        </div>
      </div>
      {showCreate && (
        <div className="flex flex-wrap items-end gap-2 border-t border-amber-200 dark:border-amber-900/60 pt-2">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-600 dark:text-neutral-400 mb-1">
              회차 이름
            </label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="예: 2026년 2회"
              className="h-8 text-sm"
            />
          </div>
          <div className="w-24">
            <label className="block text-xs text-gray-600 dark:text-neutral-400 mb-1">
              정렬 순서
            </label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={handleCreate}
            disabled={isPending}
          >
            생성
          </Button>
        </div>
      )}
      {currentSession?.is_write_locked && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          현재 회차는 글쓰기가 잠겨 일반 사용자는 작성할 수 없습니다. 관리자는
          예외적으로 작성 가능합니다.
        </p>
      )}
    </div>
  );
}
